import Header from "@/components/header";
import { AddCustomerForm } from "@/models/payload/customer";
import {
  Button,
  Container,
  Grid,
  InputBase,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useEffect } from "react";

import BirthDateSelector from "@/components/birthDateSelector";
import { Gender } from "@/models/club";
import useUpdateCustomer from "@/services/customer/hooks/useUpdateCustomer";
import { notifications } from "@mantine/notifications";
import moment from "moment-jalaali";
import { useNavigate, useParams } from "react-router-dom";
import UseGetCustomerById from "../hooks/useGetCustomerById";
import { schema } from "./type";
import useIsSmall from "@/hooks/useIsSmall";

function EditCustomer() {
  const isSmall = useIsSmall();
  const { id } = useParams();
  const { data: customer, isLoading: isLoadingCustomer } = UseGetCustomerById({
    id: Number(id),
  });
  const { mutate, isLoading } = useUpdateCustomer();
  const navigate = useNavigate();
  const form = useForm<AddCustomerForm>({
    initialValues: {
      phoneNumber: "",
      firstName: "",
      lastName: "",
      birthDate: "",
      dateOfMarriage: "",
      year: "",
      month: "",
      day: "",
      gender: null,
      // purchaseAmount: undefined,
    },
    validate: yupResolver(schema),
  });

  useEffect(() => {
    if (customer) {
      form.setValues({
        phoneNumber: customer?.phoneNumber,
        firstName: customer?.firstName,
        lastName: customer?.lastName,
      });
      form.setFieldValue("gender", customer?.gender?.toString() || "");
      form.setFieldValue(
        "year",
        customer?.birthDate ? moment(customer?.birthDate).format("jYYYY") : null
      );
      form.setFieldValue(
        "month",
        customer?.birthDate ? moment(customer?.birthDate).format("jMM") : null
      );
      form.setFieldValue(
        "day",
        customer?.birthDate ? moment(customer?.birthDate).format("jDD") : null
      );
    }
  }, [customer]);

  const submit = (values: AddCustomerForm) => {
    mutate(
      {
        ...values,
        customerId: Number(id),
        birthDate: (() => {
          const y = values?.year?.toString() || "";
          const m = values?.month?.toString() || "";
          const d = values?.day?.toString() || "";
          if (!y || !m || !d) return undefined;
          const jalali = `${y}/${m}/${d}`;
          const parsed = moment(jalali, "jYYYY/jMM/jDD", true);
          return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
        })(),
        gender: !values?.gender
          ? undefined
          : values?.gender === "0"
            ? Gender.Value0
            : Gender.Value1,
      },
      {
        onSuccess() {
          notifications.show({
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          navigate(`/dashboard/customers/details/${id}`);
        },
      }
    );
  };

  return (
    <Stack h={isSmall ? "100svh" : ""} gap="0">
      <Header title="ویرایش مشتری" showBack />
      <Container w="100%" size="md" h={isSmall ? "100svh" : ""}>
        <Title order={4}>ویرایش مشتری</Title>
        <Stack mt="lg">
          <form
            onSubmit={form.onSubmit(submit)}
            name="add-customer"
            className="w-100"
            id="add-customer"
          >
            <Stack>
              <InputBase
                size="lg"
                type="tel"
                dir="ltr"
                autoFocus
                styles={{
                  input: {
                    textAlign: "left",
                    paddingLeft: "16px",
                  },
                  label: {
                    fontSize: "1rem",
                  },
                }}
                value={form.values.phoneNumber || ""}
                maxLength={11}
                label="شماره تلفن مشتری را وارد نمایید"
                withAsterisk
                readOnly
                error={form.errors.phoneNumber}
              />

              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    size="lg"
                    styles={{
                      label: {
                        fontSize: "1rem",
                      },
                    }}
                    placeholder=""
                    label="نام"
                    {...form.getInputProps("firstName")}
                    disabled={isLoadingCustomer}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <TextInput
                    size="lg"
                    styles={{
                      label: {
                        fontSize: "1rem",
                      },
                    }}
                    placeholder=""
                    label="نام خانوادگی"
                    {...form.getInputProps("lastName")}
                    disabled={isLoadingCustomer}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Select
                    size="lg"
                    placeholder=""
                    styles={{
                      label: {
                        fontSize: "1rem",
                      },
                    }}
                    label="جنسیت"
                    {...form.getInputProps("gender")}
                    disabled={isLoadingCustomer}
                    data={[
                      {
                        value: Gender.Value1.toString(),
                        label: "خانم",
                      },
                      {
                        value: Gender.Value0.toString(),
                        label: "مرد",
                      },
                    ]}
                  />
                </Grid.Col>

                <Grid.Col span={12}><TextInput label="معرف" value={customer?.referrer || "—"} readOnly description="ویرایش معرف پشتیبانی نمی‌شود" /></Grid.Col>
                <BirthDateSelector
                  day={form.values.day ?? undefined}
                  month={form.values.month ?? undefined}
                  year={form.values.year ?? undefined}
                  onDayChange={(value) =>
                    form.setFieldValue("day", value ?? "")
                  }
                  onMonthChange={(value) =>
                    form.setFieldValue("month", value ?? "")
                  }
                  onYearChange={(value) =>
                    form.setFieldValue("year", value ?? "")
                  }
                  disabled={isLoadingCustomer}
                  dayError={
                    typeof form.errors.day === "string"
                      ? form.errors.day
                      : undefined
                  }
                  monthError={
                    typeof form.errors.month === "string"
                      ? form.errors.month
                      : undefined
                  }
                  yearError={
                    typeof form.errors.year === "string"
                      ? form.errors.year
                      : undefined
                  }
                  size="lg"
                />
                <Grid.Col span={12}>
                  {form.errors["all-or-none-date"] && (
                    <Text>تاریخ تولد را وارد نمایید</Text>
                  )}
                </Grid.Col>
              </Grid>
            </Stack>
          </form>
        </Stack>
      </Container>
      <Container w="100%">
        <Stack>
          <Button
            type="submit"
            size="md"
            my="md"
            fullWidth
            form="add-customer"

            loading={isLoading}
          >
            ثبت
          </Button>
        </Stack>
      </Container>
    </Stack>
  );
}

export default EditCustomer;
